// The Agents place (technical design §4.5, §8.3–8.4; the owner-agents prototype): the owner's view of
// the AI agents, the inbox of what they asked a person to decide, what each may do, and the chats
// with the owner analyst (the owner's) and the support agent. The owner and the manager see the
// agents (agents.view); a dispatcher or bookkeeper sees the inbox and the support chat. Uses the books section's layout (a side list of parts, a row of tabs on a phone).

import { useEffect, useRef, useState } from 'react';
import { Header, type NavProps } from '../Nav.tsx';
import { useQuery, useToast } from '../books/ui.tsx';
import { liveStream } from '../connection/live.ts';
import { AGENT_EVENTS } from './model.ts';
import { ApprovalsInbox } from '../shared/ApprovalsInbox.tsx';
import { Chat } from './Chat.tsx';
import { Overview } from './Overview.tsx';
import { Settings } from './Settings.tsx';
import type { AgentsPart, AgentsProps } from './props.ts';
import type { AgentsList } from './types.ts';
import '../books/books.css';
import './agents.css';
import '../blueprint/inbox.css';
import { useDesign } from '../blueprint/design.ts';
import { Moved } from '../blueprint/Moved.tsx';

/** Who sees the agents themselves: whoever holds agents.view (the owner and the manager). */
const VIEWS_AGENTS = 'agents.view';

// Each part's screen title and its one line (the screen head, as in the Books place); the parts' own
// page heads are hidden inside .books-main.
const HEAD: Record<AgentsPart, [string, string]> = {
  overview: [
    'AI agents',
    'Each agent works under a role and uses only that role’s actions; every tool call is checked and audited.',
  ],
  approvals: [
    'Approvals',
    'Agents ask before they contact a customer, book work or touch money. Approving runs it as the agent, with your name.',
  ],
  settings: [
    'Agent settings',
    'For each action: automatic, ask first or never. Tighten anything; loosen only up to its maximum.',
  ],
  analyst: [
    'Ask the analyst',
    'Questions about the business, answered from the reports with the queries it ran. It only reads.',
  ],
  support: [
    'Support',
    'How to do something in Flowology. A change it suggests waits for the owner’s approval.',
  ],
};

export function AgentsScreen({
  nav,
  timeZone,
  open,
}: {
  nav: NavProps;
  timeZone?: string;
  /** A notification opens a part (Support, say). */
  open?: { part: AgentsPart; at: number };
}) {
  const seesAgents = (nav.permissions ?? []).includes(VIEWS_AGENTS);
  const bp = useDesign() === 'blueprint';
  const [part, setPart] = useState<AgentsPart>(open?.part ?? (seesAgents ? 'overview' : 'approvals'));
  useEffect(() => {
    if (open) setPart(open.part);
  }, [open?.part, open?.at]);
  const [agentKey, setAgentKey] = useState('receptionist');
  const [toast, setToast] = useToast();
  const pending = useQuery<{ approvals: unknown[] }>('approvals.list', { status: 'pending' }, [part]);
  const list = useQuery<AgentsList>('agents.list', seesAgents ? {} : null, [part]);
  // a decision made on another screen, or an agent stopped there, re-reads the counts (QA-860)
  const reread = useRef<() => void>(() => undefined);
  reread.current = () => {
    void pending.reload();
    void list.reload();
  };
  useEffect(() => {
    const es = liveStream();
    let timer: ReturnType<typeof setTimeout> | undefined;
    es.addEventListener('change', (e) => {
      const { type } = JSON.parse(e.data) as { type: string };
      if (!AGENT_EVENTS.test(type)) return;
      clearTimeout(timer);
      timer = setTimeout(() => reread.current(), 250);
    });
    return () => {
      clearTimeout(timer);
      es.close();
    };
  }, []);
  const stopped = new Map(list.data?.agents.map((a) => [a.key, a.stoppedReason]) ?? []);
  const props: AgentsProps = {
    isOwner: (nav.permissions ?? []).includes('agents.manage'),
    seesAgents,
    ...(timeZone ? { timeZone } : {}),
    toast: setToast,
    agentKey,
    go: (p, key) => {
      if (key) setAgentKey(key);
      setPart(p);
    },
  };
  const waiting = pending.data?.approvals.length ?? 0;
  const here = HEAD[part];
  const parts: { part: AgentsPart; label: string; count?: number }[] = [
    ...(seesAgents ? [{ part: 'overview' as const, label: 'Agents' }] : []),
    { part: 'approvals', label: 'Approvals', count: waiting },
    ...(seesAgents ? [{ part: 'settings' as const, label: 'Agent settings' }] : []),
    ...(props.isOwner ? [{ part: 'analyst' as const, label: 'Ask the analyst' }] : []),
    { part: 'support', label: 'Support' },
  ];
  return (
    <div className="app books agents">
      <Header nav={nav} title={here[0]} lede={here[1]} />
      <div className={`books-body${bp ? ' bp-ib' : ''}`}>
        <nav className="books-parts" aria-label="Agents">
          {parts.map((p) => (
            <button
              key={p.part}
              className={`btn quiet${part === p.part ? ' on' : ''}`}
              aria-current={part === p.part ? 'page' : undefined}
              onClick={() => setPart(p.part)}
            >
              {p.label}
              {!!p.count && (
                <span className="count" aria-label={`${p.count} waiting`}>
                  {p.count}
                </span>
              )}
            </button>
          ))}
        </nav>
        <main className="books-main">
          {part === 'overview' && seesAgents && <Overview {...props} />}
          {part === 'approvals' && (
            <ApprovalsInbox
              toast={setToast}
              title="Approvals"
              intro="Agents ask before anything that contacts a customer, books work or touches money. You can decide a request only if your role holds its action; approving runs it as the agent, with your name recorded as the approver."
              stoppedReason={(k) => stopped.get(k) ?? null}
              onChange={() => void pending.reload()}
            />
          )}
          {/* Blueprint: agent settings are Setup's now (the setup audit); for a release, a way there */}
          {part === 'settings' && seesAgents && !bp && <Settings key={agentKey} {...props} />}
          {part === 'settings' && seesAgents && bp && (
            <Moved what="Agent settings" to="Setup › AI agents" href="#/settings/agents" />
          )}
          {part === 'analyst' && props.isOwner && (
            <Chat
              props={props}
              agentKey="analyst"
              title="Ask the analyst"
              intro="Questions about the business, answered from the reports (profit and loss, receivables, job costing) with the queries it ran. It only reads."
              examples={['How is this month going?', 'Who owes us money?']}
            />
          )}
          {part === 'support' && (
            <Chat
              props={props}
              agentKey="support"
              title="Support"
              intro="How to do something in Flowology. The support agent reads the settings and explains them; a change it suggests waits for the owner’s approval."
              examples={['How long after a job is the rating text sent?', 'Where do I set the trip charge?']}
            />
          )}
        </main>
      </div>
      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
}
